import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchKpiSlaViewDistribution,
  type SlaViewBucketKey,
  type SlaViewTargetScope,
  type SlaViewTypeDistributionDto,
} from './kpiDashboardApi';
import KpiSlaBucketBarChart from './charts/KpiSlaBucketBarChart';
import KpiSlaTicketsTable, {
  type SlaViewOrganizeBy,
  type SlaViewPieTicketType,
} from './KpiSlaTicketsTable';

const SLA_SUB_VIEWS: Array<{ id: SlaViewOrganizeBy; label: string }> = [
  { id: 'team', label: 'By Team' },
  { id: 'customer', label: 'By Customer' },
];

const BUCKET_LABELS: Record<SlaViewBucketKey, string> = {
  '0-24': '0–24%',
  '25-49': '25–49%',
  '50-74': '50–74%',
  '75-99': '75–99%',
  breached: 'Breached – 100%',
};

const EMPTY_DIST = (
  ticketType: SlaViewPieTicketType,
  targetScope: SlaViewTargetScope,
  label: string
): SlaViewTypeDistributionDto => ({
  ticketType,
  targetScope,
  label,
  total: 0,
  buckets: [],
});

interface SlaBarSelection {
  ticketType: SlaViewPieTicketType;
  targetScope: SlaViewTargetScope;
  bucket: SlaViewBucketKey;
  chartLabel: string;
}

export interface KpiSlaViewTabProps {
  filterValues: Record<string, string | string[]>;
  filterKey: string;
}

/**
 * KPI Dashboard — SLA View: bar charts; bar click hides charts and shows ticket table
 * (same overlay pattern as Dashboard drill-down).
 */
const KpiSlaViewTab: React.FC<KpiSlaViewTabProps> = ({ filterValues, filterKey }) => {
  const [organizeBy, setOrganizeBy] = useState<SlaViewOrganizeBy>('team');
  const [selection, setSelection] = useState<SlaBarSelection | null>(null);
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  useEffect(() => {
    setSelection(null);
  }, [filterKey]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['kpi-sla-view-distribution', filterKey],
    queryFn: () => fetchKpiSlaViewDistribution(filterValues, timezoneOffset),
    staleTime: 30_000,
  });

  const handleBucketClick = (
    ticketType: SlaViewPieTicketType,
    targetScope: SlaViewTargetScope,
    chartLabel: string,
    bucket: SlaViewBucketKey
  ) => {
    setSelection({ ticketType, targetScope, bucket, chartLabel });
  };

  const clearSelection = () => setSelection(null);

  const drillTitle = selection
    ? `${selection.chartLabel} — ${BUCKET_LABELS[selection.bucket]} (${
        organizeBy === 'team' ? 'By Team' : 'By Customer'
      })`
    : '';

  const charts: Array<{
    key: string;
    dist: SlaViewTypeDistributionDto;
    ticketType: SlaViewPieTicketType;
    targetScope: SlaViewTargetScope;
  }> = [
    {
      key: 'case',
      dist: data?.case ?? EMPTY_DIST('CASE', 'ANY', 'Case SLA'),
      ticketType: 'CASE',
      targetScope: 'ANY',
    },
    {
      key: 'incident-resolution',
      dist:
        data?.incidentResolution ??
        EMPTY_DIST('INCIDENT', 'RESOLUTION', 'Incident Resolution SLA'),
      ticketType: 'INCIDENT',
      targetScope: 'RESOLUTION',
    },
    {
      key: 'incident-ongoing',
      dist:
        data?.incidentOngoing ?? EMPTY_DIST('INCIDENT', 'ONGOING', 'Incident Ongoing SLA'),
      ticketType: 'INCIDENT',
      targetScope: 'ONGOING',
    },
    {
      key: 'request-resolution',
      dist:
        data?.requestResolution ??
        EMPTY_DIST('SERVICE_REQUEST', 'RESOLUTION', 'SR Resolution SLA'),
      ticketType: 'SERVICE_REQUEST',
      targetScope: 'RESOLUTION',
    },
    {
      key: 'request-ongoing',
      dist:
        data?.requestOngoing ??
        EMPTY_DIST('SERVICE_REQUEST', 'ONGOING', 'SR Ongoing SLA'),
      ticketType: 'SERVICE_REQUEST',
      targetScope: 'ONGOING',
    },
  ];

  if (selection) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4 pt-2 sm:px-5">
        <KpiSlaTicketsTable
          title={drillTitle}
          organizeBy={organizeBy}
          filterValues={filterValues}
          filterKey={`${filterKey}-sla-${organizeBy}-${selection.ticketType}-${selection.targetScope}-${selection.bucket}`}
          ticketType={selection.ticketType}
          targetScope={selection.targetScope}
          bucket={selection.bucket}
          onClose={clearSelection}
          backButtonLabel="Back to charts"
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-4 pb-8 pt-2 sm:px-5">
      <div className="shrink-0 rounded-xl border border-slate-200/90 bg-white px-3 py-2 shadow-sm ring-1 ring-slate-950/[0.04]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div
            className="inline-flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1"
            role="tablist"
            aria-label="SLA View organization"
          >
            {SLA_SUB_VIEWS.map((v) => {
              const selected = organizeBy === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setOrganizeBy(v.id)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    selected
                      ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
          {isFetching && !isLoading ? (
            <span className="text-[11px] font-medium text-indigo-600">Updating…</span>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="shrink-0 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          {error instanceof Error ? error.message : 'Failed to load SLA View.'}
          <button
            type="button"
            onClick={() => void refetch()}
            className="ml-3 font-medium text-red-800 underline"
          >
            Retry
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3 lg:items-stretch">
        {charts.map((c) => (
          <KpiSlaBucketBarChart
            key={c.key}
            distribution={c.dist}
            selectedBucket={null}
            onBucketClick={(bucket) =>
              handleBucketClick(c.ticketType, c.targetScope, c.dist.label, bucket)
            }
            isLoading={isLoading}
          />
        ))}
      </div>

      <p className="pb-2 text-center text-xs text-slate-500">
        Select a bar to view matching tickets with SLA Laps / Remaining.
      </p>
    </div>
  );
};

export default KpiSlaViewTab;
